/** The report itself, as the reader shows it (item 71): its summary as a lead
 *  paragraph, then each section - a chart under the trend and the
 *  organizations, and every table drawn here so its figures line up at the
 *  column's end and a change carries its arrow (which way) and its colour
 *  (good news green, bad news red - more cost is not green), as the headline
 *  tiles do. The report's own
 *  language decides its direction; the downloads keep the document as written. */
import { Fragment, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '@/hooks/useLanguage';
import remarkGfm from 'remark-gfm';
import { ReportOrganizationsChart, ReportTrendChart, type ChartFacts } from './ReportCharts';
import type { Block, Cell, ParsedReport } from './reportSections';

const MD_COMPONENTS = {
  a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

function Markdown({ text }: { text: string }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{text}</ReactMarkdown>;
}

const ARROW = { up: '▲', down: '▼' } as const;
const EMPTY: Cell = { text: '', emphasis: false, tone: null, verdict: null };

function ReportTable({ block }: { block: Extract<Block, { kind: 'table' }> }) {
  const { header, figures, rows } = block;
  return (
    <div className="rp-table" role="region" tabIndex={0} aria-label={header.join(' · ')}>
      <table>
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={`${h}-${i}`} scope="col" className={figures[i] ? 'is-num' : undefined}><bdi dir="auto">{h}</bdi></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className={row[0]?.emphasis ? 'is-other' : undefined}>
              {header.map((_, i) => {
                const cell = row[i] ?? EMPTY;
                if (i === 0) return <th key={i} scope="row"><bdi dir="auto">{cell.text}</bdi></th>;
                const className = [figures[i] ? 'is-num' : '', cell.verdict ? `is-${cell.verdict}` : ''].filter(Boolean).join(' ');
                return (
                  <td key={i} className={className || undefined}>
                    {cell.tone
                      ? <><span className="rp-arrow" aria-hidden="true">{ARROW[cell.tone]}</span> {cell.text}</>
                      : figures[i] ? cell.text : <bdi dir="auto">{cell.text}</bdi>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface Props {
  /** The report as the reader read it once (parseReport). */
  report: ParsedReport;
  language: 'en' | 'he';
  facts: ChartFacts | null;
}

/** Memoised: the library refreshes every few seconds while a report is written; this does not change. */
export const ReportBody = memo(function ReportBody({ report, language, facts }: Props) {
  const { t } = useLanguage();
  // The day-by-day table under a chart that already shows it: one click away, not a screen of rows.
  const charted = (key: string | null) => key === 'trend' && (facts?.series?.points?.length || 0) >= 2;
  return (
    <div className="rp-md" dir={language === 'he' ? 'rtl' : 'ltr'} lang={language}>
      {report.lead && <div className="rp-md__lead"><Markdown text={report.lead} /></div>}
      {report.sections.map((section, s) => (
        <section key={`${section.heading}-${s}`} className={`rp-section${section.key ? ` rp-section--${section.key}` : ''}`}
                 aria-labelledby={`rp-sec-${s}`}>
          <h2 id={`rp-sec-${s}`}>{section.heading}</h2>
          {section.key === 'trend' && <ReportTrendChart facts={facts} language={language} />}
          {section.key === 'organizations' && <ReportOrganizationsChart facts={facts} language={language} />}
          {section.blocks.map((block, b) => (
            <Fragment key={b}>
              {block.kind === 'text' ? <Markdown text={block.text} />
                : charted(section.key) ? (
                  <details className="rp-details">
                    <summary>{t('insights.reports.chart.showTable', { lng: language })}</summary>
                    <ReportTable block={block} />
                  </details>
                ) : <ReportTable block={block} />}
            </Fragment>
          ))}
        </section>
      ))}
    </div>
  );
});
